import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { AsOf } from "../components/AsOf";

const Row = ({ team, kenya, i, prefix }) => (
  <tr className="border-b border-zinc-900" data-testid={`${prefix}-row-${i}`}>
    <td className="mono px-4 py-3 text-sm text-white">{team.country}</td>
    <td className="mono px-4 py-3 text-xs text-ochre">#{team.rank}</td>
    <td className="mono px-4 py-3 text-xs text-zinc-400">{team.africa_rank ? `#${team.africa_rank}` : "—"}</td>
    <td className="mono px-4 py-3 text-xs text-zinc-400">{team.points}</td>
    <td
      className={`mono px-4 py-3 text-xs ${
        team.gap_points > 0 ? "text-maasai" : team.gap_points < 0 ? "text-emerald-500" : "text-zinc-600"
      }`}
    >
      {kenya && team.gap_points !== null
        ? `${team.gap_points > 0 ? "+" : ""}${team.gap_points}`
        : "—"}
    </td>
  </tr>
);

const Table = ({ title, note, rows, kenya, prefix }) => (
  <section className="mt-12" data-testid={`${prefix}-section`}>
    <h2 className="display-3 border-b border-white/15 pb-4">{title}</h2>
    <p className="body-copy mt-5 max-w-3xl">{note}</p>
    <div className="mt-6 overflow-x-auto border border-zinc-800">
      <table className="w-full min-w-[560px] border-collapse text-left">
        <thead>
          <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
            {["Nation", "World", "Africa", "Points", "Points ahead of Kenya"].map((h) => (
              <th key={h} className="px-4 py-3 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-300">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((team, i) => (
            <Row key={team.code} team={team} kenya={kenya} i={i} prefix={prefix} />
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default function RankingAmbition() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Ranking Ambition — where Kenya could rank if this plan lands",
    description:
      "The FIFA ranking is a formula, not an opinion. Three scenarios of the plan, run at nine competitive matches a year for six years, read back against today's world table and against comparable African nations.",
    image: cardUrl("ambition"),
  });

  useEffect(() => {
    api.get("/ranking-ambition")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the ranking model. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="ambition-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="ambition-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="ranking-ambition-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The national game</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <AsOf
            testId="ambition-source-stamp"
            kind="official"
            asOf={d.as_of}
            publisher={`FIFA world table · ${d.world_teams} nations, ${d.africa_teams} in Africa`}
            url={d.sources[0]?.url}
          />
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="ambition-headlines">
          {[
            ["Kenya today", d.kenya ? `#${d.kenya.rank}` : "—", d.kenya ? `${d.kenya.points} points · #${d.kenya.africa_rank} in Africa` : ""],
            ["If the plan lands in full", d.target ? `#${d.target.projected_world_rank}` : "—", d.target ? `${d.target.projected_points} points · #${d.target.projected_africa_rank} in Africa` : ""],
            ["Places gained", d.target ? `${d.target.places_gained > 0 ? "+" : ""}${d.target.places_gained}` : "—", `over ${d.horizon_years} years`],
            ["Matches it turns on", `${d.matches_per_year * d.horizon_years}`, `${d.matches_per_year} competitive matches a year`],
          ].map(([label, value, note], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`ambition-headline-${i}`}>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
              <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
              <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
            </div>
          ))}
        </div>

        <section className="mt-12" data-testid="ambition-scenarios">
          <h2 className="display-3 border-b border-white/15 pb-4">Three Scenarios, One Formula</h2>
          <div className="mt-6 grid gap-px bg-zinc-800 md:grid-cols-3">
            {d.scenarios.map((s, i) => (
              <div key={s.key} className="bg-[#0b0b0b] p-6" data-testid={`ambition-scenario-${s.key}`}>
                <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-500">
                  Scenario {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-4 font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
                  {s.label}
                </p>
                <p className="mt-5 font-display text-4xl font-bold leading-none text-ochre">
                  #{s.projected_world_rank}
                </p>
                <p className="mono mt-2 text-[0.56rem] uppercase tracking-[0.14em] text-zinc-500">
                  {s.projected_points} points · #{s.projected_africa_rank} in Africa
                </p>
                <dl className="mt-6 space-y-2 border-t border-zinc-900 pt-4">
                  {[
                    ["Matches won", `${Math.round(s.won * 100)}%`],
                    ["Matches drawn", `${Math.round(s.drawn * 100)}%`],
                    ["Points a match", `${s.points_per_match > 0 ? "+" : ""}${s.points_per_match}`],
                    ["Points gained", `${s.points_gained > 0 ? "+" : ""}${s.points_gained}`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-4">
                      <dt className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">{k}</dt>
                      <dd className="mono text-xs text-white">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-5 text-xs leading-relaxed text-zinc-400">{s.note}</p>
                {s.peers_overtaken.length ? (
                  <p className="mono mt-4 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-maasai">
                    Ahead of {s.peers_overtaken.join(", ")}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <Table
          title="The Nations We Are Actually Measured Against"
          note="East African neighbours and similarly sized football economies. A positive number is how far ahead of Kenya they sit today."
          rows={d.peers}
          kenya={d.kenya}
          prefix="ambition-peers"
        />

        <Table
          title="Where The Game Is Already Professional"
          note="Not the peer group — the destination. Every one of these nations built a domestic structure before its ranking moved."
          rows={d.benchmarks}
          kenya={d.kenya}
          prefix="ambition-benchmarks"
        />

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="ambition-method">            <p className="overline text-ochre">How this is calculated</p>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.method}</p>
          </div>
          <div>
            <div className="border border-zinc-800 bg-[#0b0b0b] p-6" data-testid="ambition-control-case">
              <p className="overline text-ochre">The control case: {d.control_case.country}</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-300">{d.control_case.note}</p>
            </div>
            <div className="mt-6 border border-zinc-800" data-testid="ambition-sources">
              {d.sources.map((s, i) => (
                <a
                  key={s.url + i}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-4 border-b border-zinc-900 bg-[#0b0b0b] p-4 transition-colors hover:bg-[#141414]"
                  data-testid={`ambition-source-${i}`}
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-white">{s.label}</span>
                    <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                      {s.publisher} · {s.kind}
                    </span>
                  </span>
                  <ExternalLink size={12} className="shrink-0 text-ochre" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/harambee-fixtures" className="btn-primary" data-testid="ambition-fixtures-link">
            Fixtures and results <ArrowRight size={14} />
          </Link>
          <Link to="/fifa-ranking" className="btn-ghost" data-testid="ambition-ranking-link">
            Kenya in the FIFA ranking
          </Link>
          <Link to="/national-game" className="btn-ghost" data-testid="ambition-national-link">
            The national game plan
          </Link>
        </div>
      </div>
    </div>
  );
}
